import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Area, AreaChart, ComposedChart, Bar, BarChart, ReferenceArea
} from 'recharts';
import { format, subDays, startOfToday, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import MainLayout from '@/components/layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Calendar } from 'lucide-react';
import { deviceStore } from '@/stores/deviceStore';
import { chartStore, Sampling } from '@/stores/chartStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// Custom styles for better touch interaction
import './DeviceChartsPage.css';

// Custom bar component for Gantt chart
const CustomBar = (props: any) => {
  const { x, y, width, height, fill, value } = props;
  return value ? <rect x={x} y={y} width={width} height={height} fill={fill} /> : null;
};

// Custom dot component that only displays non-zero values
const CustomizedDot = (props: any) => {
  const { cx, cy, value } = props;
  
  // Only render the dot if value is truthy (non-zero)
  if (!value) {
    return null;
  }

  return (
    <circle cx={cx} cy={cy} r={4} fill={props.stroke} />
  );
};

// Custom line that only connects non-zero points
const NonZeroLine = (props: {
  points: Array<{
    x: number;
    y: number;
    payload?: any;
  }>;
  dataKey: string;
  [key: string]: any;
}) => {
  const { points, ...restProps } = props;
  
  // Group consecutive non-zero points
  const segments: Array<Array<{x: number; y: number; payload?: any}>> = [];
  let currentSegment: Array<{x: number; y: number; payload?: any}> = [];
  
  points.forEach(point => {
    if (point.payload && point.payload[props.dataKey]) {
      currentSegment.push(point);
    } else if (currentSegment.length > 0) {
      segments.push([...currentSegment]);
      currentSegment = [];
    }
  });
  
  // Add the last segment if it exists
  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }
  
  return (
    <>
      {segments.map((segment, i) => {
        // Only render if at least 2 points (to form a line)
        if (segment.length < 2) return null;
        
        return (
          <path
            key={`line-${i}`}
            d={`M ${segment.map(p => `${p.x},${p.y}`).join(' L ')}`}
            fill="none"
            {...restProps}
          />
        );
      })}
    </>
  );
};

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Format the label (timestamp) as DD/MM/YYYY HH:mm if it's a date
    let formattedLabel = label;
    try {
      // Check if label looks like a timestamp from our chart data
      const timeRegex = /^\d{2}:\d{2}$|^\d{2}:\d{2}:\d{2}$|^[A-Za-z]{3} \d{2}$/;
      if (typeof label === 'string' && timeRegex.test(label)) {
        // Try to get the actual timestamp from payload
        const timestamp = payload[0]?.payload?.timestamp;
        if (timestamp && typeof timestamp === 'string') {
          const date = parseISO(timestamp);
          formattedLabel = format(date, 'dd/MM/yyyy HH:mm');
        }
      }
    } catch (e) {
      // If parsing fails, use the original label
      console.log('Error formatting tooltip date:', e);
    }

    return (
      <div className="bg-white p-3 sm:p-4 rounded-md shadow-lg border border-gray-200 text-sm sm:text-base max-w-[90vw] sm:max-w-xs">
        <p className="font-semibold text-sm sm:text-base mb-1">{formattedLabel}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${index}`} style={{ color: entry.color }} className="flex justify-between items-center gap-2">
            <span className="font-medium truncate">{entry.name}:</span>
            <span>
              {entry.value !== undefined && entry.value !== null ? 
                (typeof entry.value === 'number' ? 
                  (entry.dataKey === 'csq' ? `${entry.value.toFixed(0)} RSSI` : 
                   (entry.dataKey === 'nrg' || entry.dataKey === 'consumed_nrg' ? 
                     `${entry.value.toFixed(2)} kWh` : entry.value.toFixed(2))) 
                  : entry.value)
                : 'N/A'
              }
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Athens timezone for all date/time operations
const TIMEZONE = 'Europe/Athens';

// Format time based on sampling
const formatTime = (time: string, sampling: Sampling) => {
  if (!time) return '';
  
  // The API now returns timestamps already converted to Europe/Athens timezone
  // We just need to format the display according to the sampling
  
  // First parse the timestamp - it might already include timezone info from API
  const date = parseISO(time);
  
  // Simply format the date - since API handles timezone conversions
  switch (sampling) {
    case '15min':
      return format(date, 'HH:mm');
    case '1h':
      return format(date, 'HH:00');
    case '1d':
      return format(date, 'MMM dd');
    default:
      return format(date, 'yyyy-MM-dd HH:mm');
  }
};

const DeviceChartsPage = observer(() => {
  const { t } = useTranslation();
  const today = startOfToday();
  
  // Detect mobile devices
  const isMobile = useMediaQuery('(max-width: 640px)');
  
  // Device selection state
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  
  // Search term for filtering devices
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI state for collapsible sections
  const [deviceListCollapsed, setDeviceListCollapsed] = useState(false);
  const [dateRangeCollapsed, setDateRangeCollapsed] = useState(false);
  
  // Date selection state
  const [startDate, setStartDate] = useState<string>(format(subDays(today, 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(today, 'yyyy-MM-dd'));
  
  // Chart configuration state
  const [sampling, setSampling] = useState<Sampling>('1h');
  const [chartTab, setChartTab] = useState('energy');
  
  // Get device list from device store
  const { devices, loading: deviceLoading, error: deviceError } = deviceStore;
  
  // Get chart data from chart store
  const { chartData, loading: chartLoading, error: chartError } = chartStore;
  
  // Load devices on component mount
  useEffect(() => {
    deviceStore.loadDevices();
  }, []);
  
  // Load chart data when parameters change
  useEffect(() => {
    if (selectedDeviceIds.length > 0) {
      // The backend now handles timezone conversions, so we can pass dates in local Athens time
      // No need to convert to UTC before sending to the API
      chartStore.fetchEnergyChartData(startDate, endDate, selectedDeviceIds, sampling);
    }
  }, [selectedDeviceIds, startDate, endDate, sampling]);
  
  // Handle device selection change
  const handleDeviceSelectionChange = (deviceId: string) => {
    if (selectedDeviceIds.includes(deviceId)) {
      setSelectedDeviceIds(selectedDeviceIds.filter(id => id !== deviceId));
    } else {
      setSelectedDeviceIds([...selectedDeviceIds, deviceId]);
    }
  };
  
  // Filter devices with relay (which can have energy data)
  const devicesWithRelay = devices.filter(device => device.relay && device.relay >= 1 && device.relay <= 3);
  
  // Predefined time ranges
  const timeRanges = [
    { label: t('charts.today'), start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') },
    { label: t('charts.yesterday'), start: format(subDays(today, 1), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') },
    { label: t('charts.last7Days'), start: format(subDays(today, 7), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') },
    { label: t('charts.last30Days'), start: format(subDays(today, 30), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') },
  ];
  
  // Apply a predefined time range
  const applyTimeRange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };
  
  // Format data for charts
  const formattedData = chartData?.data?.map(point => {
    // Data coming from API has r1,r2,r3 for relay states but uses 'force' field
    // We'll create the force state values based on the 'force' property
    // Note from logs: force appears to be 0 when not forcing any relay
    const forceValue = point.force !== undefined ? Number(point.force) : 0;
    
    // Get the relevant device to determine which relay to show
    const device = devices.find(d => d.device_id === point.device_id);
    const relayNum = device?.relay || 0;
    
    // Create a result object with most properties
    const result: any = {
      ...point,
      time: formatTime(point.timestamp, sampling),
      // Force state props - must be null (not 0) when not forced to create discontinuous lines
      force: forceValue > 0 ? 1 : null,
    };
    
    // Only add the specific relay state that matches the device's relay number
    if (relayNum >= 1 && relayNum <= 3) {
      const relayKey = `r${relayNum}`;
      const relayValue = point[relayKey] !== undefined && Number(point[relayKey]) > 0 ? 1 : null;
      result[relayKey] = relayValue;
    }
    
    return result;
  }) || [];
  
  // Debug log the data for the first few points
  console.log('Chart data sample (after formatting):', formattedData.slice(0, 3));
  
  // Group consecutive ON periods for relay states to create continuous segments
  const createGanttSegments = (dataPoints: any[], relayKey: string) => {
    const segments = [];
    let currentSegment: {start: string, end: string} | null = null;
    
    for (let i = 0; i < dataPoints.length; i++) {
      const point = dataPoints[i];
      // Check for both null/undefined and boolean values
      const isOn = point[relayKey] !== undefined && point[relayKey] !== null && Boolean(point[relayKey]);
      
      if (isOn) {
        // If this is the first point or we're starting a new segment
        if (currentSegment === null) {
          currentSegment = {
            start: point.time,
            end: point.time // initial end is the same as start
          };
        }
        
        // If this is the last point, close out the segment with proper end
        if (i === dataPoints.length - 1) {
          // For the last point, extend a bit for visibility
          segments.push({
            ...currentSegment,
            end: point.time
          });
        } else {
          // Update the end to the next point's time
          currentSegment.end = dataPoints[i + 1].time;
        }
      } else {
        // If we were tracking a segment and now it's OFF
        if (currentSegment !== null) {
          // End the segment at the current point
          segments.push(currentSegment);
          currentSegment = null;
        }
      }
    }
    
    return segments;
  };
  
  // Create segments for each relay
  const r1Segments = createGanttSegments(formattedData, 'r1');
  const r2Segments = createGanttSegments(formattedData, 'r2');
  const r3Segments = createGanttSegments(formattedData, 'r3');
  const r1fSegments = createGanttSegments(formattedData, 'r1f');
  const r2fSegments = createGanttSegments(formattedData, 'r2f');
  const r3fSegments = createGanttSegments(formattedData, 'r3f');
  
  // Debug log the segments
  console.log('Relay segments:', { r1: r1Segments, r2: r2Segments, r3: r3Segments });
  
  return (
    <MainLayout>
      <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
        <Card className="overflow-hidden">
          <CardHeader className="px-4 sm:px-6">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{t('charts.title', 'Energy Charts')}</CardTitle>
                <CardDescription>{t('charts.description', 'Visualize and analyze energy consumption data')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-6">
            {deviceLoading && (
              <div className="flex justify-center items-center py-8 sm:py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            
            {deviceError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{deviceError}</AlertDescription>
              </Alert>
            )}
            
            {!deviceLoading && !deviceError && (
              <div className="space-y-6">
                {/* Filters Section */}
                <div className="space-y-6">
                  {/* Device Selection */}
                  <div className="space-y-2 border rounded-md p-3">
                    <div className="flex justify-between items-center cursor-pointer" 
                         onClick={() => setDeviceListCollapsed(!deviceListCollapsed)}>
                      <h3 className="font-medium">{t('charts.selectDevices', 'Select Devices')}</h3>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`h-5 w-5 transition-transform ${deviceListCollapsed ? 'transform rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {!deviceListCollapsed && (
                      <>
                        {devicesWithRelay.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            {t('charts.noDevicesAvailable', 'No devices with energy data available')}
                          </p>
                        ) : (
                          <>
                            {/* Search input */}
                            <div className="relative">
                              <input
                                type="text"
                                placeholder={t('charts.searchDevices', 'Search devices...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full p-2 pl-8 border rounded-md text-sm"
                              />
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-4 w-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <Button
                                variant="outline"
                                size="sm"
                                className="absolute right-1 top-1/2 transform -translate-y-1/2 text-xs"
                                onClick={() => {
                                  const filteredDevices = devicesWithRelay
                                    .filter(device => 
                                      searchTerm === '' || 
                                      device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                      (device.area && device.area.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                      (device.point && device.point.toLowerCase().includes(searchTerm.toLowerCase()))
                                    );
                                  
                                  const filteredIds = filteredDevices.map(d => d.device_id);
                                  const allSelected = filteredIds.every(id => selectedDeviceIds.includes(id));
                                  
                                  if (allSelected) {
                                    // If all are selected, unselect all filtered devices
                                    setSelectedDeviceIds(selectedDeviceIds.filter(id => !filteredIds.includes(id)));
                                  } else {
                                    // If some or none are selected, select all filtered devices
                                    const newSelection = [...new Set([...selectedDeviceIds, ...filteredIds])];
                                    setSelectedDeviceIds(newSelection);
                                  }
                                }}
                              >
                                {devicesWithRelay
                                  .filter(device => 
                                    searchTerm === '' || 
                                    device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    (device.area && device.area.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (device.point && device.point.toLowerCase().includes(searchTerm.toLowerCase()))
                                  )
                                  .every(device => selectedDeviceIds.includes(device.device_id)) 
                                  ? t('charts.unselectAll', 'Unselect All') 
                                  : t('charts.selectAll', 'Select All')}
                              </Button>
                            </div>
                            
                            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-2 mt-2">
                              {devicesWithRelay
                                // Sort devices by device_id
                                .sort((a, b) => a.device_id.localeCompare(b.device_id))
                                // Filter devices based on search term
                                .filter(device => 
                                  searchTerm === '' || 
                                  device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  (device.area && device.area.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                  (device.point && device.point.toLowerCase().includes(searchTerm.toLowerCase()))
                                )
                                .map(device => (
                                  <div key={device.device_id} className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      id={`device-${device.device_id}`}
                                      checked={selectedDeviceIds.includes(device.device_id)}
                                      onChange={() => handleDeviceSelectionChange(device.device_id)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor={`device-${device.device_id}`} className="flex-grow text-sm truncate">
                                      {device.device_id} 
                                      {device.area && <span className="text-muted-foreground ml-2">({device.area})</span>}
                                    </label>
                                  </div>
                                ))}
                            </div>
                            
                            <div className="flex justify-between items-center mt-2 text-sm">
                              <span>{selectedDeviceIds.length} {t('charts.selected', 'selected')}</span>
                              {selectedDeviceIds.length > 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setSelectedDeviceIds([])}
                                >
                                  {t('charts.clearSelection', 'Clear')}
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Date Range & Sampling */}
                  <div className="space-y-2 border rounded-md p-3">
                    <div className="flex justify-between items-center cursor-pointer"
                         onClick={() => setDateRangeCollapsed(!dateRangeCollapsed)}>
                      <h3 className="font-medium">{t('charts.dateRange', 'Date Range & Sampling')}</h3>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`h-5 w-5 transition-transform ${dateRangeCollapsed ? 'transform rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {!dateRangeCollapsed && (
                      <>
                        {/* Date Range Inputs */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                          <div className="space-y-2">
                            <label className="text-sm font-medium flex items-center" htmlFor="start-date">
                              {t('charts.from', 'From')}
                              <span className="ml-1 text-xs text-muted-foreground">(Europe/Athens)</span>
                            </label>
                            <div className="relative">
                              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="start-date"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="pl-8 w-full"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium flex items-center" htmlFor="end-date">
                              {t('charts.to', 'To')}
                              <span className="ml-1 text-xs text-muted-foreground">(Europe/Athens)</span>
                            </label>
                            <div className="relative">
                              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="end-date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="pl-8 w-full"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Predefined Time Ranges */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {timeRanges.map((range, i) => (
                            <Button 
                              key={i} 
                              variant="outline" 
                              size="sm"
                              onClick={() => applyTimeRange(range.start, range.end)}
                              className="flex-grow sm:flex-grow-0"
                            >
                              {range.label}
                            </Button>
                          ))}
                        </div>
                        
                        {/* Sampling Selection */}
                        <div className="space-y-2 mt-3">
                          <label className="text-sm font-medium">
                            {t('charts.sampling', 'Data Sampling')}
                          </label>
                          <Select value={sampling} onValueChange={(val) => setSampling(val as Sampling)}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('charts.selectSampling', 'Select sampling')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="15min">{t('charts.fifteenMinutes', '15 Minutes')}</SelectItem>
                              <SelectItem value="1h">{t('charts.hourly', 'Hourly')}</SelectItem>
                              <SelectItem value="1d">{t('charts.daily', 'Daily')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Charts Section */}
                {selectedDeviceIds.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">
                      {t('charts.selectDevicesToView', 'Select devices to view charts')}
                    </h3>
                    <p className="text-gray-500">
                      {t('charts.noDevicesSelected', 'Please select at least one device from the list above to display energy charts')}
                    </p>
                  </div>
                ) : (
                  <>
                    {chartLoading && (
                      <div className="flex justify-center items-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    
                    {chartError && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{chartError}</AlertDescription>
                      </Alert>
                    )}
                    
                    {!chartLoading && !chartError && (
                      <>
                        {chartData && chartData.data && chartData.data.length > 0 ? (
                          <div className="space-y-4">
                            <Tabs defaultValue="energy" value={chartTab} onValueChange={setChartTab}>
                              <div className="overflow-x-auto pb-2">
                                <TabsList className="min-w-max w-full sm:w-auto sm:grid sm:grid-cols-4 mb-6">
                                  <TabsTrigger value="energy">{t('charts.energyConsumption', 'Energy Consumption')}</TabsTrigger>
                                  <TabsTrigger value="signal">{t('charts.signalStrength', 'Signal Strength')}</TabsTrigger>
                                  <TabsTrigger value="relay">{t('charts.relayStatus', 'Relay Status')}</TabsTrigger>
                                  <TabsTrigger value="combined">{t('charts.combined', 'Combined View')}</TabsTrigger>
                                </TabsList>
                              </div>
                              
                              {isMobile && (
                                <div className="flex items-center justify-center gap-2 mb-2 text-xs text-muted-foreground">
                                  <svg className="h-4 w-4 swipe-hint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                  </svg>
                                  <span>{t('charts.swipeToNavigate', 'Swipe to navigate charts')}</span>
                                  <svg className="h-4 w-4 swipe-hint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                  </svg>
                                </div>
                              )}
                              
                              <TabsContent value="energy" className="space-y-6">
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.energyUsage', 'Energy Usage Over Time (kWh)')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <AreaChart data={formattedData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                      <defs>
                                        <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0.2}/>
                                        </linearGradient>
                                      </defs>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis tick={{ fontSize: 12 }} label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      <Area 
                                        type="monotone" 
                                        dataKey="nrg" 
                                        name={t('charts.energy', 'Energy (kWh)')}
                                        stroke="#8884d8" 
                                        fillOpacity={1} 
                                        fill="url(#colorEnergy)" 
                                      />
                                    </AreaChart>
                                  </ResponsiveContainer>
                                </div>
                                
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.consumedEnergy', 'Consumed Energy (kWh)')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <BarChart data={formattedData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis tick={{ fontSize: 12 }} label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      <Bar 
                                        dataKey="consumed_nrg" 
                                        name={t('charts.consumed', 'Consumed (kWh)')}
                                        fill="#82ca9d" 
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </TabsContent>
                              
                              <TabsContent value="signal" className="space-y-6">
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.signalQuality', 'Signal Quality (RSSI)')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <LineChart data={formattedData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis domain={[0, 31]} tick={{ fontSize: 12 }} label={{ value: 'RSSI', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      <Line 
                                        type="monotone" 
                                        dataKey="csq" 
                                        name={t('charts.signal', 'Signal (RSSI)')}
                                        stroke="#ff7300" 
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 6 }} 
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              </TabsContent>
                              
                              <TabsContent value="relay" className="space-y-6">
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.relayStates', 'Relay States')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <LineChart
                                      data={formattedData}
                                      margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis domain={[0, 1.2]} ticks={[0, 1]} tickFormatter={(value) => value === 0 ? 'OFF' : 'ON'} tick={{ fontSize: 12 }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      
                                      {/* Only show relay 1 for devices with relay=1 */}
                                      {devicesWithRelay.some(d => d.relay === 1 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r1" 
                                          name={`${t('charts.relay', 'Relay')} 1`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 2 for devices with relay=2 */}
                                      {devicesWithRelay.some(d => d.relay === 2 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r2" 
                                          name={`${t('charts.relay', 'Relay')} 2`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 3 for devices with relay=3 */}
                                      {devicesWithRelay.some(d => d.relay === 3 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r3" 
                                          name={`${t('charts.relay', 'Relay')} 3`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                                
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.forceStates', 'Force States')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <LineChart
                                      data={formattedData}
                                      margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis domain={[0, 1.2]} ticks={[0, 1]} tickFormatter={(value) => value === 0 ? 'OFF' : 'ON'} tick={{ fontSize: 12 }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      
                                      {/* Force state */}
                                      <Line 
                                        dataKey="force" 
                                        name={t('charts.force', 'Force State')} 
                                        stroke="#00C853"
                                        strokeWidth={4}
                                        strokeDasharray="5 5"
                                        type="stepAfter"
                                        dot={false}
                                        activeDot={{ r: 6 }}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              </TabsContent>
                              
                              <TabsContent value="combined" className="space-y-6">
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.combinedView', 'Combined View')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <ComposedChart 
                                      data={formattedData}
                                      margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                                      <YAxis 
                                        yAxisId="right" 
                                        orientation="right" 
                                        domain={[0, 1.2]} 
                                        ticks={[0, 1]} 
                                        tickFormatter={(value) => value === 0 ? 'OFF' : 'ON'} 
                                        tick={{ fontSize: 12 }}
                                      />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />

                                      {/* Energy area chart */}
                                      <Area 
                                        yAxisId="left"
                                        dataKey="nrg" 
                                        name={t('charts.energy', 'Energy (kWh)')}
                                        fill="#8884d8" 
                                        stroke="#8884d8"
                                        fillOpacity={0.3} 
                                        isAnimationActive={false}
                                      />
                                      
                                      {/* Signal strength */}
                                      <Line 
                                        yAxisId="left"
                                        type="monotone" 
                                        dataKey="csq" 
                                        name={t('charts.signal', 'Signal (RSSI)')}
                                        stroke="#ff7300" 
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 6 }}
                                        isAnimationActive={false}
                                      />
                                      
                                      {/* Only show relay 1 for devices with relay=1 */}
                                      {devicesWithRelay.some(d => d.relay === 1 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          yAxisId="right"
                                          dataKey="r1" 
                                          name={`${t('charts.relay', 'Relay')} 1`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 2 for devices with relay=2 */}
                                      {devicesWithRelay.some(d => d.relay === 2 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          yAxisId="right"
                                          dataKey="r2" 
                                          name={`${t('charts.relay', 'Relay')} 2`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 3 for devices with relay=3 */}
                                      {devicesWithRelay.some(d => d.relay === 3 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          yAxisId="right"
                                          dataKey="r3" 
                                          name={`${t('charts.relay', 'Relay')} 3`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                    </ComposedChart>
                                  </ResponsiveContainer>
                                </div>
                                
                                <div className="bg-white rounded-lg p-2 sm:p-4 border">
                                  <h3 className="font-medium mb-2 sm:mb-4">{t('charts.allRelaysView', 'All Relays View')}</h3>
                                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} minHeight={300}>
                                    <LineChart 
                                      data={formattedData}
                                      margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                                      <YAxis domain={[0, 1.2]} ticks={[0, 1]} tickFormatter={(value) => value === 0 ? 'OFF' : 'ON'} tick={{ fontSize: 12 }} />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend wrapperStyle={{ fontSize: 12 }} />
                                      
                                      {/* Only show relay 1 for devices with relay=1 */}
                                      {devicesWithRelay.some(d => d.relay === 1 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r1" 
                                          name={`${t('charts.relay', 'Relay')} 1`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 2 for devices with relay=2 */}
                                      {devicesWithRelay.some(d => d.relay === 2 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r2" 
                                          name={`${t('charts.relay', 'Relay')} 2`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Only show relay 3 for devices with relay=3 */}
                                      {devicesWithRelay.some(d => d.relay === 3 && selectedDeviceIds.includes(d.device_id)) && (
                                        <Line 
                                          dataKey="r3" 
                                          name={`${t('charts.relay', 'Relay')} 3`} 
                                          stroke="#00C853"
                                          strokeWidth={6}
                                          type="stepAfter"
                                          dot={false}
                                          activeDot={{ r: 6 }}
                                          isAnimationActive={false}
                                          connectNulls={false}
                                        />
                                      )}
                                      
                                      {/* Force state */}
                                      <Line 
                                        dataKey="force"
                                        name={t('charts.force', 'Force State')}
                                        stroke="#00C853"
                                        strokeWidth={4}
                                        strokeDasharray="5 5"
                                        type="stepAfter"
                                        dot={false}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              </TabsContent>
                            </Tabs>
                            
                            <div className="sm:hidden text-center text-xs text-muted-foreground py-2">
                              <span>{t('charts.swipeHint', 'Swipe left/right to pan charts')}</span>
                            </div>
                            
                            <div className="text-xs sm:text-sm text-muted-foreground">
                              {t('charts.dataPoints', 'Data Points')}: {chartData?.metadata?.count || 0} |  
                              {t('charts.sampling', 'Sampling')}: {chartData?.metadata?.sampling || '-'} |  
                              {t('charts.dateRange', 'Date Range')}: {chartData?.metadata?.date_from || '-'} - {chartData?.metadata?.date_to || '-'}
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-8 text-center">
                            <h3 className="text-lg font-medium text-gray-700 mb-2">
                              {t('charts.noDataAvailable', 'No data available')}
                            </h3>
                            <p className="text-gray-500">
                              {t('charts.tryDifferentCriteria', 'Try selecting a different date range or devices')}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
});

export default DeviceChartsPage; 